import { useAtom } from 'jotai'
import { useSearchParams } from 'react-router-dom'
import {
  sellTokenAtom,
  sellTokenAmountAtom,
  buyTokenAtom,
  buyTokenAmountAtom,
} from '../stores/trade-store'
import { useEffect } from 'react'
import useEffectCompare from 'src/common/hooks/helpers/useEffectCompare'
import useOnFirstMount from 'src/common/hooks/helpers/useOnFirstMount'
import { KNOWN_TOKENS } from 'src/config/tokens/known-tokens'
import useActiveOrDefaultChain from 'src/common/hooks/web3/useActiveOrDefaultChain'
import { big } from 'src/common/utils/big-utils'
import useToken from 'src/common/apollo/hooks/useToken'

const useSyncTradeSearchParams = () => {
  const chain = useActiveOrDefaultChain()

  const [sellToken, setSellToken] = useAtom(sellTokenAtom)
  const [sellTokenAmount, setSellTokenAmount] = useAtom(sellTokenAmountAtom)
  const [buyTokenAmount, setBuyTokenAmount] = useAtom(buyTokenAmountAtom)

  const [buyToken, setBuyToken] = useAtom(buyTokenAtom)

  const [searchParams, setSearchParams] = useSearchParams()

  const sellTokenAddressParam = searchParams.get('sellToken') ?? ''
  const buyTokenAddressParam = searchParams.get('buyToken') ?? ''
  const sellTokenAmountParam = searchParams.get('sellTokenAmount') ?? ''
  const buyTokenAmountParam = searchParams.get('buyTokenAmount') ?? ''

  const { token: sellTokenMetadata } = useToken(sellTokenAddressParam, {
    skip: Boolean(sellToken && !sellTokenAddressParam),
  })
  const { token: buyTokenMetadata } = useToken(buyTokenAddressParam, {
    skip: Boolean(buyToken && !buyTokenAddressParam),
  })

  const resetTradeSearchParams = () => {
    searchParams.delete('sellToken')
    searchParams.delete('sellTokenAmount')
    searchParams.delete('buyToken')
    searchParams.delete('buyTokenAmount')
    searchParams.set('chain', chain.name!)
    setSearchParams(searchParams)
  }

  useOnFirstMount(() => {
    if (!sellTokenAddressParam && !sellToken) {
      setSellToken(KNOWN_TOKENS[chain.id].USDC)
    }
    if (sellTokenAddressParam) {
      setSellTokenAmount(big(sellTokenAmountParam))
    }
    if (buyTokenAddressParam) {
      setBuyTokenAmount(big(buyTokenAmountParam))
    }
    searchParams.set('chain', chain.name!)
    setSearchParams(searchParams)
  })

  useEffectCompare(() => {
    if (sellTokenMetadata) {
      setSellToken(sellTokenMetadata)
    }
    if (buyTokenMetadata) {
      setBuyToken(buyTokenMetadata)
    }
  }, [sellTokenMetadata, buyTokenMetadata])

  useEffect(() => {
    const updatedParams: Record<string, string> = {}

    if (sellToken?.contractAddress) {
      searchParams.set('sellToken', sellToken.id)
    }
    if (sellTokenAmount && sellTokenAmount.gt(0)) {
      searchParams.set('sellTokenAmount', sellTokenAmount.toString())
    }
    if (buyToken?.id) {
      searchParams.set('buyToken', buyToken.id)
    }
    if (buyTokenAmount && buyTokenAmount.gt(0)) {
      searchParams.set('buyTokenAmount', buyTokenAmount.toString())
    }

    setSearchParams(updatedParams)
    setSearchParams(searchParams)
  }, [
    buyToken,
    buyTokenAmount,
    searchParams,
    sellToken,
    sellTokenAmount,
    setSearchParams,
  ])

  useEffectCompare(() => {
    resetTradeSearchParams()
  }, [chain.id])
}

export default useSyncTradeSearchParams
